// #/app/formtest/page.tsx
'use client';

import React, { useEffect, useState } from 'react';
import Select, { ActionMeta, components, GroupProps } from 'react-select';
import { ZodSchema } from 'zod';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';

import DynamicDataList from '~/forms/DynamicDataList';
import DynamicForm from '~/forms/DynamicForm';
import { Button } from '~/ui/button';
import { DataList } from '~/ui/data-list';

import { useFlowContext } from '#/providers/FlowContext';
import { listServiceKeys } from '#/services/registry/listServiceKeys';
import { getServiceDetails } from '#/services/registry/getServiceDetails';
import { getServiceActionKeys } from '#/services/registry/getServiceActionKeys';
import {
  createZodSchemaFromJsonSchema,
  zodSchemasFromActionDetails,
} from '#/services/registry/zodFromJson';
import { CronDefaultInputValues, CronInputSchema } from '#/services/workspaces';
import { ServiceDetails, ActionDetails } from '#/types/registry';
import { Nullable } from '#/types/utils/nullable';
import { ActionNodeData, TriggerNodeData } from '#/types/workspace';

// ```
// utils/generateZodSchema.ts
// import { z } from 'zod';
// import { extendZodWithOpenApi } from 'zod-openapi';
// import {
//   jsonSchemaToZod,
//   jsonSchemaToZodDereffed,
//   parseSchema,
// } from 'json-schema-to-zod';

// export async function generateZodSchemaFromActionDetails(actionDetails) {
//   const _module = jsonSchemaToZod(actionDetails);
//   console.debug({ _module });
//   const dereffed = await jsonSchemaToZodDereffed(actionDetails);
//   console.debug({ dereffed });
//   const schema = parseSchema(actionDetails);
//   console.debug({ schema });
//   return schema;
// }
// ```

interface Option {
  value: string;
  label: string;
}
function optionsFromStringArray(
  ary: string[],
  customMapping: Record<string, any> | null = null,
) {
  // console.debug({ ary });
  return (
    orderBy(
      ary,
      (str) => customMapping == null || customMapping[str] != null,
      'desc',
    )
      // .filter((str) => customMapping == null || customMapping[str] != null)
      .map((str) => ({
        value: str,
        label: customMapping ? customMapping[str] ?? str : str,
        // value: service.path,
        // label: service.name,
      }))
  );
}
const customServiceLabelMapping = {
  openai: 'Open AI (Chat GPT)',
  'twitter-v2': 'Twitter',
};
function optionsFromServiceKeys(serviceKeys: string[]) {
  return optionsFromStringArray(serviceKeys, customServiceLabelMapping);
}
const customActionLabelMapping = {
  'post /chat/completions': 'prompt (post /chat/completions)',
  'post /2/tweets': 'tweet (post /2/tweets)',
};
function optionsFromActionKeys(actionKeys: string[]) {
  return optionsFromStringArray(actionKeys, customActionLabelMapping);
}

// class ErrorBoundary extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = { error: null, hasError: false };
//   }

//   static getDerivedStateFromError(error) {
//     // Update state so the next render will show the fallback UI.
//     return { error, hasError: true };
//   }

//   componentDidCatch(error, errorInfo) {
//     // You can also log the error to an error reporting service
//     // console.error(error, errorInfo);
//   }

//   render() {
//     if (this.state.hasError) {
//       // You can render any custom fallback UI
//       return <h1>Error: {error.toString()}</h1>;
//     }

//     return this.props.children;
//   }
// }

function TriggerDetailsForm(props: { nodeData: TriggerNodeData }) {
  const { nodeData } = props;

  const { updateWorkflowNodeData } = useFlowContext();

  const [isPathInputsLoading, setPathInputsLoading] = useState(false);

  const triggerOptions = optionsFromStringArray(['cron']);
  const [selectedTriggerKey, setSelectedTriggerKey] = useState(
    nodeData?.triggerType ?? 'cron',
  );
  const triggerInputsZodSchema = createZodSchemaFromJsonSchema(
    nodeData?.inputSchema ?? selectedTriggerKey === 'cron'
      ? CronInputSchema
      : null,
  );
  const nodeDataInputValues =
    nodeData?.inputValues ?? selectedTriggerKey === 'cron'
      ? CronDefaultInputValues
      : null;

  const handleTriggerChange = async (
    option: Option | null,
    actionMeta: ActionMeta<Option>,
  ) => {
    const _triggerKey = option?.value ?? 'cron';
    if (_triggerKey === selectedTriggerKey) return;
    setSelectedTriggerKey(_triggerKey);
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-col space-y-2">
        <label className="text-primary">Trigger</label>
        <Select
          options={triggerOptions}
          defaultValue={triggerOptions.find(
            (opt) => opt.value === selectedTriggerKey,
          )}
          onChange={handleTriggerChange}
          // isDisabled
        />
      </div>
      <div>
        <div className="">
          <div className="flex flex-col space-y-4">
            <div>
              <h2 className="mb-2 text-primary">Trigger Inputs</h2>
              <DynamicForm
                schema={triggerInputsZodSchema}
                defaultValues={nodeDataInputValues}
                // form
                formProps={{
                  // TODO: save on change?
                  onChange: (ev) => {
                    // console.debug(
                    //   'onChange:',
                    //   ev,
                    //   ev.target,
                    //   ev.target.value,
                    // );
                  },
                }}
                onSubmit={async (values) => {
                  // console.debug('onSubmit:', values);
                  setPathInputsLoading(true);
                  try {
                    await updateWorkflowNodeData({ inputValues: values });
                  } finally {
                    setPathInputsLoading(false);
                  }
                }}
                // props
                renderAfter={({ submit }) => (
                  <Button
                    onClick={(e) => {
                      console.debug(e);
                      e.preventDefault();
                      submit();
                    }}
                    size="lg"
                    className="mt-2 text-primary hover:cursor-pointer"
                    type="submit"
                  >
                    {isPathInputsLoading ? (
                      <span className="loading" />
                    ) : (
                      'Save Values'
                    )}
                  </Button>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingIndicator() {
  return <p className="m-auto text-primary">Loading...</p>;
}

function ActionDetailsForm(props: { nodeData: Partial<ActionNodeData> }) {
  const { nodeData } = props;

  const { updateWorkflowNodeData } = useFlowContext();

  const { type: nodeDataType, serviceKey, actionKey } = nodeData ?? {};

  const [selectedServiceKey, setSelectedServiceKey] = useState<string | null>(
    serviceKey ?? null,
  );
  const [selectedActionKey, setSelectedActionKey] = useState<string | null>(
    actionKey ?? null,
  );

  const [isLoadingServiceKeys, setIsLoadingServiceKeys] = useState(true);
  const [serviceKeys, setServiceKeys] = useState<string[]>([]);
  const [serviceOptions, setServiceOptions] = useState<Option[]>([]);

  async function fetchServices() {
    try {
      setIsLoadingServiceKeys(true);
      const _serviceKeys = await listServiceKeys();
      console.debug({ _serviceKeys });
      setServiceKeys(_serviceKeys);
      setServiceOptions(optionsFromServiceKeys(_serviceKeys));
    } finally {
      setIsLoadingServiceKeys(false);
    }
  }

  useEffect(() => {
    fetchServices();
  }, []);

  const [isLoadingServiceDetails, setIsLoadingServiceDetails] = useState(false);
  const [serviceDetails, setServiceDetails] =
    useState<Partial<ServiceDetails> | null>(null);

  const [actions, setActions] = useState<string[]>([]);
  const [actionOptions, setActionOptions] = useState<Option[]>([]);

  const [isChangingService, setIsChangingService] = useState(false);

  const handleServiceChange = async (
    option: Option | null,
    actionMeta: ActionMeta<Option>,
  ) => {
    try {
      const _serviceKey = option?.value;
      if (_serviceKey === serviceKey || _serviceKey == null) {
        return;
      }
      setIsChangingService(true);
      setSelectedServiceKey(_serviceKey);
      setSelectedActionKey(null);
    } finally {
      setIsChangingService(false);
    }
  };

  async function reloadDataOnNewServiceSelected() {
    try {
      if (isEmpty(selectedServiceKey)) {
        return;
      }
      setIsLoadingServiceDetails(true);
      const _serviceDetails = await getServiceDetails(selectedServiceKey!);
      console.debug('_serviceDetails:', _serviceDetails);
      setServiceDetails(_serviceDetails);
      const _actions = await getServiceActionKeys(selectedServiceKey!);
      console.debug({ _actions });
      setActions(_actions);
      setActionOptions(optionsFromActionKeys(_actions));
    } finally {
      setIsLoadingServiceDetails(false);
    }
  }

  useEffect(() => {
    reloadDataOnNewServiceSelected();
  }, [selectedServiceKey]);

  const [isLoadingActionDetails, setIsLoadingActionDetails] = useState(false);
  const [actionDetails, setActionDetails] = useState<ActionDetails | null>(
    null,
  );

  const [isPathInputsLoading, setIsPathInputsLoading] = useState(false);

  const [inputSchema, setInputSchema] = useState(null);
  const [defaultInputValues, setDefaultInputValues] = useState(null);
  const [outputSchema, setOutputSchema] = useState(null);

  const [actionPathInputsZodSchema, setActionPathInputsZodSchema] =
    useState<Nullable<ZodSchema>>(null);
  const [actionQueryInputsZodSchema, setActionQueryInputsZodSchema] =
    useState<Nullable<ZodSchema>>(null);
  const [actionRequestBodyJsonZodSchema, setActionRequestBodyJsonZodSchema] =
    useState<Nullable<ZodSchema>>(null);
  const [actionResponseJsonZodSchema, setActionResponseJsonZodSchema] =
    useState<Nullable<ZodSchema>>(null);

  const [isChangingAction, setIsChangingAction] = useState(false);

  const handleActionChange = async (
    option: Option | null,
    actionMeta: ActionMeta<Option>,
  ) => {
    try {
      const _actionKey = option?.value;
      if (_actionKey === actionKey || _actionKey == null) {
        return;
      }
      setIsChangingAction(true);
      setSelectedActionKey(_actionKey);
    } finally {
      setIsChangingAction(false);
    }
  };

  async function reloadDataOnNewActionSelected() {
    try {
      if (isEmpty(selectedServiceKey) || isEmpty(selectedActionKey)) {
        return;
      }

      setIsLoadingActionDetails(true);

      let _actionDetails;
      try {
        const response = await fetch(
          `/api/registry/getActionDetails?${new URLSearchParams({
            serviceKey: selectedServiceKey!,
            actionKey: selectedActionKey!,
          })}`,
        );
        _actionDetails = await response.json();
      } catch (error: any) {
        console.error(error);
      }
      console.debug('_actionDetails:', _actionDetails);
      setActionDetails(_actionDetails);

      const {
        serviceKey,
        actionKey,
        actionVerb,
        actionEndpoint,
        inputSchema = {},
        defaultInputValues = {},
        outputSchema = {},
      } = _actionDetails ?? {};
      const { headers, path, query, bodyJson } = inputSchema;

      const zodSchemas = zodSchemasFromActionDetails(_actionDetails);

      setInputSchema(inputSchema);
      setActionPathInputsZodSchema(zodSchemas?.inputSchema?.path ?? null);
      setActionQueryInputsZodSchema(zodSchemas?.inputSchema?.query ?? null);
      setActionRequestBodyJsonZodSchema(
        zodSchemas?.inputSchema?.bodyJson ?? null,
      );

      setDefaultInputValues(defaultInputValues);

      setOutputSchema(outputSchema);
      setActionResponseJsonZodSchema(zodSchemas?.outputSchema ?? null);

      await updateWorkflowNodeData({
        type: nodeDataType,
        serviceKey: selectedServiceKey!,
        actionKey,
        actionVerb,
        actionEndpoint,
        inputSchema,
        inputValues: nodeData?.inputValues ?? defaultInputValues,
        outputSchema,
      });
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsLoadingActionDetails(false);
    }
  }

  useEffect(() => {
    reloadDataOnNewActionSelected();
  }, [selectedActionKey]);

  // const isLoading =
  //   isLoadingServiceKeys ||
  //   isChangingService ||
  //   isLoadingServiceDetails ||
  //   isChangingAction ||
  //   isLoadingActionDetails;

  const Group = (props: GroupProps) => (
    <div>
      <components.Group {...props} />
    </div>
  );

  if (isLoadingServiceKeys) {
    return (
      <div className="flex flex-col h-full py-4">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full py-4">
      {isLoadingServiceKeys ? (
        <LoadingIndicator />
      ) : (
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col space-y-2">
            <label className="text-primary">Service</label>
            <Select
              options={serviceOptions}
              defaultValue={serviceOptions.find(
                (opt) => opt.value === selectedServiceKey,
              )}
              onChange={handleServiceChange}
              components={{ Group }}
            />
          </div>
          {isChangingService || isLoadingServiceDetails ? (
            <LoadingIndicator />
          ) : selectedServiceKey == null ? (
            <></>
          ) : (
            <>
              <div>
                {!isEmpty(serviceDetails) ? (
                  <DataList
                    items={{
                      Name: serviceDetails.name,
                      Description: serviceDetails.description,
                    }}
                  />
                ) : null}
              </div>
              {!isEmpty(selectedServiceKey) ? (
                <div className="flex flex-col space-y-2">
                  <label className="text-primary">Action</label>
                  <Select
                    options={actionOptions}
                    defaultValue={actionOptions.find(
                      (opt) => opt.value === selectedActionKey,
                    )}
                    onChange={handleActionChange}
                    components={{ Group }}
                  />
                </div>
              ) : null}
              <div>
                {isChangingAction || isLoadingActionDetails ? (
                  <div className="flex flex-col py-4">
                    <LoadingIndicator />
                  </div>
                ) : selectedActionKey == null ? (
                  <></>
                ) : (
                  <div className="">
                    <div className="flex flex-col space-y-4">
                      <div>
                        <h2 className="text-primary">Path Inputs</h2>
                        {!isEmpty(selectedActionKey) &&
                        !isEmpty(actionPathInputsZodSchema) ? (
                          <DynamicForm
                            schema={actionPathInputsZodSchema}
                            defaultValues={nodeData?.inputValues?.path}
                            // form
                            formProps={{
                              // TODO: save on change?
                              onChange: (ev) => {
                                // console.debug(
                                //   'onChange:',
                                //   ev,
                                //   ev.target,
                                //   ev.target.value,
                                // );
                              },
                            }}
                            onSubmit={async (values) => {
                              console.debug('onSubmit:', values);
                              setIsPathInputsLoading(true);
                              try {
                                await updateWorkflowNodeData({
                                  type: nodeDataType,
                                  serviceKey: selectedServiceKey!,
                                  actionKey: selectedActionKey!,
                                  inputValues: {
                                    ...(nodeData?.inputValues ?? {}),
                                    path: values,
                                  },
                                });
                              } finally {
                                setIsPathInputsLoading(false);
                              }
                            }}
                            // props
                            renderAfter={({ submit }) => (
                              <Button
                                onClick={(e) => {
                                  console.debug(e);
                                  e.preventDefault();
                                  submit();
                                }}
                                size="lg"
                                className="mt-2 text-primary hover:cursor-pointer"
                                type="submit"
                              >
                                {isPathInputsLoading ? (
                                  <span className="loading" />
                                ) : (
                                  'Save Values'
                                )}
                              </Button>
                            )}
                          />
                        ) : (
                          <div className="flex flex-col py-4">
                            <p className="text-primary">None.</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <h2 className="text-primary">Query Inputs</h2>
                        {!isEmpty(selectedActionKey) &&
                        !isEmpty(actionQueryInputsZodSchema) ? (
                          <DynamicForm
                            schema={actionQueryInputsZodSchema}
                            defaultValues={nodeData?.inputValues?.query}
                            // form
                            formProps={{
                              // TODO: save on change?
                              onChange: (ev) => {
                                // console.debug(
                                //   'onChange:',
                                //   ev,
                                //   ev.target,
                                //   ev.target.value,
                                // );
                              },
                            }}
                            onSubmit={async (values) => {
                              console.debug('onSubmit:', values);
                              setIsPathInputsLoading(true);
                              try {
                                await updateWorkflowNodeData({
                                  type: nodeDataType,
                                  serviceKey: selectedServiceKey!,
                                  actionKey: selectedActionKey!,
                                  inputValues: {
                                    ...(nodeData?.inputValues ?? {}),
                                    query: values,
                                  },
                                });
                              } finally {
                                setIsPathInputsLoading(false);
                              }
                            }}
                            // props
                            renderAfter={({ submit }) => (
                              <Button
                                onClick={(e) => {
                                  console.debug(e);
                                  e.preventDefault();
                                  submit();
                                }}
                                size="lg"
                                className="mt-2 text-primary hover:cursor-pointer"
                                type="submit"
                              >
                                {isPathInputsLoading ? (
                                  <span className="loading" />
                                ) : (
                                  'Save Values'
                                )}
                              </Button>
                            )}
                          />
                        ) : (
                          <div className="flex flex-col py-4">
                            <p className="text-primary">None.</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <h2 className="text-primary">
                          Request Body JSON Inputs
                        </h2>
                        {!isEmpty(selectedActionKey) &&
                        !isEmpty(actionRequestBodyJsonZodSchema) ? (
                          <DynamicForm
                            schema={actionRequestBodyJsonZodSchema}
                            defaultValues={nodeData?.inputValues?.bodyJson}
                            // form
                            formProps={{
                              // TODO: save on change?
                              onChange: (ev) => {
                                // console.debug(
                                //   'onChange:',
                                //   ev,
                                //   ev.target,
                                //   ev.target.value,
                                // );
                              },
                            }}
                            onSubmit={async (values) => {
                              console.debug('onSubmit:', values);
                              setIsPathInputsLoading(true);
                              try {
                                await updateWorkflowNodeData({
                                  type: nodeDataType,
                                  serviceKey: selectedServiceKey!,
                                  actionKey: selectedActionKey!,
                                  inputValues: {
                                    ...(nodeData?.inputValues ?? {}),
                                    bodyJson: values,
                                  },
                                });
                              } finally {
                                setIsPathInputsLoading(false);
                              }
                            }}
                            // props
                            renderAfter={({ submit }) => (
                              <Button
                                onClick={(e) => {
                                  console.debug(e);
                                  e.preventDefault();
                                  submit();
                                }}
                                size="lg"
                                className="mt-2 text-primary hover:cursor-pointer"
                                type="submit"
                              >
                                {isPathInputsLoading ? (
                                  <span className="loading" />
                                ) : (
                                  'Save Values'
                                )}
                              </Button>
                            )}
                          />
                        ) : (
                          <div className="flex flex-col py-4">
                            <p className="text-primary">None.</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <h2 className="text-primary">Response JSON</h2>
                        {!isEmpty(selectedActionKey) &&
                        !isEmpty(actionResponseJsonZodSchema) ? (
                          <DynamicDataList
                            schema={actionResponseJsonZodSchema}
                          />
                        ) : (
                          <div className="flex flex-col py-4">
                            <p className="text-primary">None.</p>
                          </div>
                        )}
                      </div>

                      {/*
                        <div className="space-x-4">
                          <Button
                            size="lg"
                            className="mt-2 text-primary"
                            type="submit"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={onSubmit}
                            size="lg"
                            className="mt-2 text-primary"
                            type="submit"
                          >
                            Submit
                          </Button>
                        </div>
                      */}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function NodeDetailsForm({ nodeData }) {
  const {
    type: nodeDataType,
    serviceKey,
    actionKey,
    inputSchema = {},
    outputSchema = {},
  } = nodeData ?? {};

  // const [nodeDataType, setNodeDataType] = useState(nodeData?.type);
  // console.debug('[NodeDetailsForm] nodeDataType:', nodeDataType);

  const [isLoading, setIsLoading] = useState(false);

  const { updateWorkflowNodeData } = useFlowContext();

  // const {
  //   register,
  //   handleSubmit,
  //   formState: { errors },
  // } = useForm({
  //   resolver: zodResolver(zodSchema),
  // });

  // const onSubmit = (data: any) => {
  //   console.log(data);
  // };

  // if (isLoading) {
  //   return (
  //     <div className="flex flex-col h-full py-4">
  //       <LoadingIndicator />
  //     </div>
  //   );
  // }

  async function handleChangeNodeDataType(
    option: Option | null,
    actionMeta: ActionMeta<Option>,
  ) {
    try {
      const newNodeDataType = option?.value;
      if (nodeDataType === newNodeDataType || newNodeDataType == null) {
        return;
      }
      console.debug(
        '[NodeDetailsForm][handleChangeNodeDataType] option:',
        JSON.stringify(option),
      );
      setIsLoading(true);
      await updateWorkflowNodeData({
        type: newNodeDataType,
      });
      // setNodeDataType(newNodeDataType);
    } finally {
      setIsLoading(false);
    }
  }
  // function handleChangeNodeDataType(option) {
  //   console.debug('[NodeDetailsForm][node data type onChange] option:', option);
  //   if (option?.value == null || option?.value === nodeDataType) return;
  //   setNodeDataType(option?.value);
  // }

  const nodeTypeOptions = [
    { value: 'trigger', label: 'Trigger' },
    { value: 'action', label: 'Action' },
  ];

  return (
    <div className="w-full flex flex-col space-y-4">
      <div className="w-full flex flex-col space-y-2">
        <label className="text-primary">Node Type</label>
        <Select
          options={nodeTypeOptions}
          defaultValue={nodeTypeOptions.find(
            (opt) => opt.value === nodeDataType,
          )}
          onChange={handleChangeNodeDataType}
          filterOption={null}
          blurInputOnSelect
          closeMenuOnSelect
          closeMenuOnScroll
          // onFocus={(ev) => ev.target.value}
        />
      </div>
      {isLoading ? (
        <div className="flex flex-col h-full py-4">
          <LoadingIndicator />
        </div>
      ) : nodeDataType === 'trigger' ? (
        <TriggerDetailsForm
          {...{
            nodeData: { type: nodeDataType, ...nodeData },
          }}
        />
      ) : (
        <ActionDetailsForm
          {...{
            nodeData: { type: nodeDataType, ...nodeData },
          }}
        />
      )}
    </div>
  );
}
